import { TFunction } from "i18next";

import { RecurringType, TodoType } from "constant/todo";

import { TodoItemData } from './item';

const MONTH_TRANSLATION_KEYS = [
  "todoList.months.january",
  "todoList.months.february",
  "todoList.months.march",
  "todoList.months.april",
  "todoList.months.may",
  "todoList.months.june",
  "todoList.months.july",
  "todoList.months.august",
  "todoList.months.september",
  "todoList.months.october",
  "todoList.months.november",
  "todoList.months.december",
] as const;

// Shared date/time types and validation
export interface DateTimeFields {
  day: string | null;
  month: string | null;
  year: string | null;
  time: string;
}

export interface DateTimeErrors {
  day?: boolean;
  month?: boolean;
  year?: boolean;
  time?: boolean;
  endDay?: boolean;
  endMonth?: boolean;
  endYear?: boolean;
  endTime?: boolean;
}

export const validateDateTimeFields = (
  dateTime: DateTimeFields,
  endDateTime?: DateTimeFields,
  isInterval?: boolean,
): DateTimeErrors => {
  const errors: DateTimeErrors = {};

  // Validate primary date/time
  if (!dateTime.day) errors.day = true;
  if (!dateTime.month) errors.month = true;
  if (!dateTime.year) errors.year = true;
  if (!dateTime.time) errors.time = true;

  // Validate end date/time for interval
  if (isInterval && endDateTime) {
    if (!endDateTime.day) errors.endDay = true;
    if (!endDateTime.month) errors.endMonth = true;
    if (!endDateTime.year) errors.endYear = true;
    if (!endDateTime.time) errors.endTime = true;

    // Validate end is not before start (only if all fields are filled)
    if (
      dateTime.day &&
      dateTime.month &&
      dateTime.year &&
      dateTime.time &&
      endDateTime.day &&
      endDateTime.month &&
      endDateTime.year &&
      endDateTime.time
    ) {
      const sameDate =
        dateTime.day === endDateTime.day &&
        dateTime.month === endDateTime.month &&
        dateTime.year === endDateTime.year;

      const [startH, startM] = dateTime.time.split(':').map(Number);
      const [endH, endM] = endDateTime.time.split(':').map(Number);

      if (sameDate) {
        // Same date - only check time
        if (endH * 60 + endM < startH * 60 + startM) {
          errors.endTime = true;
        }
      } else {
        // Different dates - check full date/time
        const startDate = new Date(
          Number(dateTime.year),
          Number(dateTime.month) - 1,
          Number(dateTime.day),
          startH,
          startM,
        );
        const endDate = new Date(
          Number(endDateTime.year),
          Number(endDateTime.month) - 1,
          Number(endDateTime.day),
          endH,
          endM,
        );

        if (endDate < startDate) {
          errors.endDay = true;
          errors.endMonth = true;
          errors.endYear = true;
          errors.endTime = true;
        }
      }
    }
  }

  return errors;
};

export const hasDateTimeErrors = (errors: DateTimeErrors): boolean =>
  Object.values(errors).some(Boolean);

export const getDateOnly = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const getTodoDate = (todo: TodoItemData): Date | null => {
  // Specijalna logika za INTERVAL tip
  if (todo.timeType === 'INTERVAL' && todo.startTime && todo.endTime) {
    const today = getDateOnly(new Date());
    const start = getDateOnly(new Date(todo.startTime));
    const end = getDateOnly(new Date(todo.endTime));

    // Pre intervala → grupiši po početnom datumu
    if (today < start) {
      return new Date(todo.startTime);
    }
    // Unutar intervala → prikaži kao "DANAS"
    if (today >= start && today <= end) {
      return new Date();
    }
    // Posle intervala → grupiši po krajnjem datumu (za "U kašnjenju")
    return new Date(todo.endTime);
  }

  // Za DEADLINE i EXACT_TIME
  const dateStr = todo.dueDate || todo.startTime;
  return dateStr ? new Date(dateStr) : null;
};

export interface TodoGroupLabels {
  overdue: string;
  today: string;
  tomorrow: string;
  dayAfterTomorrow: string;
  months: string[];
}

export const createMonthOptions = (t: TFunction) =>
  MONTH_TRANSLATION_KEYS.map((key, index) => ({
    value: String(index + 1),
    label: t(key),
  }));

export const createTodoTypeLabels = (t: TFunction): Record<TodoType, string> => ({
  CALL: t("todoList.types.call"),
  MEETING: t("todoList.types.meeting"),
  INTERNAL_TASK: t("todoList.types.internalTask"),
});

export const createTodoTypeOptions = (t: TFunction) => {
  const labels = createTodoTypeLabels(t);

  return (["CALL", "MEETING", "INTERNAL_TASK"] as TodoType[]).map((type) => ({
    value: type,
    label: labels[type],
  }));
};

export const createRecurringTypeLabels = (t: TFunction): Record<RecurringType, string> => ({
  DAILY: t("todoList.recurring.daily"),
  WEEKLY: t("todoList.recurring.weekly"),
  MONTHLY: t("todoList.recurring.monthly"),
});

export const createTodoGroupLabels = (t: TFunction): TodoGroupLabels => ({
  overdue: t("todoList.groups.overdue"),
  today: t("todoList.groups.today"),
  tomorrow: t("todoList.groups.tomorrow"),
  dayAfterTomorrow: t("todoList.groups.dayAfterTomorrow"),
  months: createMonthOptions(t).map((option) => option.label),
});

export const formatDateTitle = (date: Date, months: string[]): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.getMonth();
  const year = date.getFullYear();
  return `${day}. ${months[month]} ${year}.`;
};

export interface TodoGroup {
  key: string;
  title: string;
  subtitle?: string;
  isOverdue?: boolean;
  todos: TodoItemData[];
}

export const groupTodosByDate = (
  todos: TodoItemData[],
  labels: TodoGroupLabels,
): TodoGroup[] => {
  const today = getDateOnly(new Date());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const dayAfterTomorrow = new Date(today);
  dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);

  const groups: Map<string, TodoGroup> = new Map();

  // Sort todos by date first
  const sortedTodos = [...todos].sort((a, b) => {
    const dateA = getTodoDate(a);
    const dateB = getTodoDate(b);
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    return dateA.getTime() - dateB.getTime();
  });

  sortedTodos.forEach((todo) => {
    const todoDate = getTodoDate(todo);
    if (!todoDate) return;

    const todoDateOnly = getDateOnly(todoDate);
    let groupKey: string;
    let title: string;
    let subtitle: string | undefined;
    let isOverdue = false;

    if (todoDateOnly < today) {
      groupKey = 'overdue';
      title = labels.overdue;
      isOverdue = true;
    } else if (todoDateOnly.getTime() === today.getTime()) {
      groupKey = 'today';
      title = labels.today;
      subtitle = formatDateTitle(today, labels.months);
    } else if (todoDateOnly.getTime() === tomorrow.getTime()) {
      groupKey = 'tomorrow';
      title = labels.tomorrow;
      subtitle = formatDateTitle(tomorrow, labels.months);
    } else if (todoDateOnly.getTime() === dayAfterTomorrow.getTime()) {
      groupKey = 'dayafter';
      title = labels.dayAfterTomorrow;
      subtitle = formatDateTitle(dayAfterTomorrow, labels.months);
    } else {
      groupKey = todoDateOnly.toISOString();
      title = formatDateTitle(todoDateOnly, labels.months);
    }

    if (!groups.has(groupKey)) {
      groups.set(groupKey, { key: groupKey, title, subtitle, isOverdue, todos: [] });
    }
    groups.get(groupKey)!.todos.push(todo);
  });

  // Return groups in order: overdue, today, tomorrow, dayafter, then future dates
  const order = ['overdue', 'today', 'tomorrow', 'dayafter'];
  const result: TodoGroup[] = [];

  order.forEach((key) => {
    if (groups.has(key)) {
      result.push(groups.get(key)!);
      groups.delete(key);
    }
  });

  // Add remaining groups (future dates) sorted by date
  const remaining = Array.from(groups.values()).sort(
    (a, b) => new Date(a.key).getTime() - new Date(b.key).getTime(),
  );
  result.push(...remaining);

  return result;
};
